// src/components/Store/ProductCard.jsx
// Tarjeta de resultado estilo marketplace: foto sobre blanco, título,
// precio grande, entrega gratis, urgencia de inventario y botón de carrito.
import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TruckIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { productName } from '../../utils/store';
import Price from './Price';

const NEW_DAYS = 30;
const LOW_STOCK = 5;

export const isNewProduct = (product) =>
  product.createdAt && Date.now() - new Date(product.createdAt).getTime() < NEW_DAYS * 24 * 60 * 60 * 1000;

const ProductCard = ({ product, qty, maxQty, onChangeQty }) => {
  const { t, i18n } = useTranslation('common');
  const name = productName(product, i18n.language);
  const href = `/store/product/${product._id}`;
  const lowStock = product.qtyLeft !== null && product.qtyLeft !== undefined && product.qtyLeft <= LOW_STOCK;

  return (
    <article className="group flex flex-col bg-white rounded-lg border border-[#3A2A20]/10 overflow-hidden transition-shadow duration-200 hover:shadow-[0_4px_20px_rgba(58,42,32,0.12)]">
      <Link to={href} className="relative block aspect-square bg-white p-3 sm:p-4">
        {product.images?.[0] ? (
          <img
            src={product.images[0]}
            alt={name}
            loading="lazy"
            className="h-full w-full object-contain transition-transform duration-300 group-hover:scale-[1.03]"
          />
        ) : (
          <div className="h-full w-full rounded bg-[#EFE6DD] flex items-center justify-center text-xs text-[#3A2A20]/30">
            {t('bookingForm.noImage')}
          </div>
        )}
        {isNewProduct(product) && (
          <span className="absolute top-2 left-2 px-2 py-0.5 rounded-sm bg-[#9A0002] text-[#EFE6DD] text-[11px] font-bold uppercase tracking-wide">
            {t('store.new')}
          </span>
        )}
        {lowStock && (
          <span className="absolute top-2 right-2 px-2 py-0.5 rounded-sm bg-[#7A0001] text-white text-[11px] font-semibold">
            {t('store.sellingFast')}
          </span>
        )}
      </Link>

      <div className="flex flex-1 flex-col gap-1.5 border-t border-[#3A2A20]/5 p-3 sm:p-4">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-[#3A2A20]/50">
          {t(`store.categories.${product.category}`)}
        </span>
        <Link
          to={href}
          className="text-sm sm:text-[15px] leading-snug text-[#3A2A20] line-clamp-2 hover:text-[#9A0002] hover:underline"
        >
          {name}
        </Link>

        <Price amount={product.price} className="mt-1" />

        <p className="flex items-start gap-1 text-xs text-[#3A2A20]/70">
          <TruckIcon className="h-4 w-4 shrink-0 text-[#9A0002]" aria-hidden="true" />
          <span>
            <strong className="font-semibold text-[#3A2A20]">{t('store.freeDelivery')}</strong> {t('store.withYourStay')}
          </span>
        </p>

        {lowStock && (
          <p className="text-xs font-medium text-[#9A0002]">{t('store.onlyLeft', { count: product.qtyLeft })}</p>
        )}

        <div className="mt-auto pt-3">
          {qty === 0 ? (
            <button
              type="button"
              onClick={() => onChangeQty(product._id, 1)}
              className="w-full rounded-full bg-[#9A0002] py-2 text-sm font-medium text-[#EFE6DD] shadow-sm transition hover:bg-[#7A0001] active:scale-[0.98]"
            >
              {t('store.addToCartLong')}
            </button>
          ) : (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between rounded-full border-2 border-[#9A0002] bg-white">
                <button
                  type="button"
                  onClick={() => onChangeQty(product._id, -1)}
                  aria-label={t('store.decrease')}
                  className="h-8 w-10 rounded-full text-lg font-semibold text-[#9A0002] hover:bg-[#9A0002]/10"
                >
                  −
                </button>
                <span className="text-sm font-semibold text-[#3A2A20]">{qty}</span>
                <button
                  type="button"
                  onClick={() => onChangeQty(product._id, 1)}
                  disabled={qty >= maxQty}
                  aria-label={t('store.increase')}
                  className="h-8 w-10 rounded-full text-lg font-semibold text-[#9A0002] hover:bg-[#9A0002]/10 disabled:opacity-30"
                >
                  +
                </button>
              </div>
              <p className="flex items-center justify-center gap-1 text-[11px] font-medium text-[#3A6B4C]">
                <CheckCircleIcon className="h-3.5 w-3.5" aria-hidden="true" />
                {t('store.inCart')}
              </p>
            </div>
          )}
        </div>
      </div>
    </article>
  );
};

export default ProductCard;
